import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentMode } from '@/contexts/PaymentModeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Play, ArrowLeft, Calendar, Star, Loader2, Lock, Clock, Flag, DollarSign, Film, Users, User, Globe, Clock4 } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MovieDetail() {
  const { movieId } = useParams();
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const paymentMode = usePaymentMode();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extendingTrial, setExtendingTrial] = useState(false);
  const [error, setError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  useEffect(() => {
    const fetchMovie = async () => {
      try {
        const response = await axios.get(`${API}/movies/${movieId}`);
        setMovie(response.data);
      } catch (err) {
        setError('Film non trouve');
      } finally {
        setLoading(false);
      }
    };

    fetchMovie();
  }, [movieId]);

  const handleExtendTrial = async () => {
    try {
      setExtendingTrial(true);
      const response = await axios.post(
        `${API}/extend-trial`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh user to update trial_extended flag
      await refreshUser();
      
      // Force component re-render
      window.location.reload();
      
    } catch (err) {
      alert(err.response?.data?.detail || 'Prolongation impossible');
      setExtendingTrial(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!reportType) {
      alert('Veuillez sélectionner un type de problème');
      return;
    }
    
    try {
      setReportSubmitting(true);
      
      const problemMessages = {
        'lien_casse': 'Problème avec le lien du film',
        'publicites': 'Trop de publicités',
        'mauvais_contenu': 'Le film ne correspond pas au titre'
      };
      
      await axios.post(
        `${API}/reports`,
        {
          content_id: movieId,
          content_type: 'movie',
          content_title: movie?.title || 'Film inconnu',
          problem_type: reportType,
          message: problemMessages[reportType]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('✅ Signalement envoyé avec succès ! Merci pour votre aide.');
      setShowReportModal(false);
      setReportType('');
    } catch (err) {
      alert('Erreur lors de l\'envoi du signalement');
    } finally {
      setReportSubmitting(false);
    }
  };

  const handlePlay = async () => {
    // Si auth requis et pas connecté, rediriger vers login
    if (paymentMode.auth_required && !user) {
      navigate('/login');
      return;
    }
    
    // Si mode payant et pas d'accès
    if (paymentMode.enabled && user && !user.has_access) {
      navigate('/compte');
      return;
    }
    
    // Naviguer vers le lecteur intégré
    navigate(`/regarder/${movieId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-netflix-red animate-spin" />
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bebas text-white mb-4">{error || 'Film non trouve'}</h1>
          <Button onClick={() => navigate('/')} className="bg-netflix-red hover:bg-netflix-red-hover">
            Retour a l'accueil
          </Button>
        </div>
      </div>
    );
  }

  const backdropUrl = movie.backdrop_url || movie.poster_url;
  const posterUrl = movie.poster_url || 'https://via.placeholder.com/500x750?text=No+Image';
  const title = movie.title || 'Sans titre';
  const overview = movie.overview || 'Aucun synopsis disponible pour ce film.';
  const rating = movie.vote_average ? movie.vote_average.toFixed(1) : 'N/A';
  const releaseYear = movie.release_date ? new Date(movie.release_date).getFullYear() : 'N/A';
  
  // In free mode, everyone has access
  const needsAccess = paymentMode.enabled && !user?.has_access;
  const canExtendTrial = paymentMode.enabled && user?.is_trial && !user?.has_access && !user?.trial_extended;

  return (
    <div className="min-h-screen bg-bg-primary">
      
      {/* Modal de signalement */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-bg-secondary border border-white/20 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bebas text-white mb-4">Signaler un problème</h3>
            <p className="text-text-secondary font-inter text-sm mb-4">
              Quel problème rencontrez-vous avec ce film ?
            </p>
            
            <div className="space-y-3 mb-6">
              <button
                onClick={() => setReportType('lien_casse')}
                className={`w-full p-3 rounded-lg border-2 transition-all ${
                  reportType === 'lien_casse'
                    ? 'border-netflix-red bg-netflix-red/20 text-white'
                    : 'border-white/20 hover:border-white/40 text-text-secondary hover:text-white'
                }`}
              >
                <p className="font-work font-semibold">Problème avec le lien du film</p>
                <p className="text-xs mt-1 opacity-70">Le lecteur ne se lance pas ou erreur</p>
              </button>
              
              <button
                onClick={() => setReportType('publicites')}
                className={`w-full p-3 rounded-lg border-2 transition-all ${
                  reportType === 'publicites'
                    ? 'border-netflix-red bg-netflix-red/20 text-white'
                    : 'border-white/20 hover:border-white/40 text-text-secondary hover:text-white'
                }`}
              >
                <p className="font-work font-semibold">J'ai des publicités</p>
                <p className="text-xs mt-1 opacity-70">Trop de popups ou pubs intrusives</p>
              </button>
              
              <button
                onClick={() => setReportType('mauvais_contenu')}
                className={`w-full p-3 rounded-lg border-2 transition-all ${
                  reportType === 'mauvais_contenu'
                    ? 'border-netflix-red bg-netflix-red/20 text-white'
                    : 'border-white/20 hover:border-white/40 text-text-secondary hover:text-white'
                }`}
              >
                <p className="font-work font-semibold">Le film ne correspond pas au titre</p>
                <p className="text-xs mt-1 opacity-70">Mauvais film ou contenu différent</p>
              </button>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowReportModal(false);
                  setReportType('');
                }}
                variant="outline"
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmitReport}
                disabled={!reportType || reportSubmitting}
                className="flex-1 bg-netflix-red hover:bg-netflix-red-hover"
              >
                {reportSubmitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="fixed left-4 z-50" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-black/80 hover:bg-black/90 backdrop-blur-sm rounded-full transition-colors text-white font-work font-semibold"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour
        </button>
      </div>

      <div className="relative w-full h-[50vh] md:h-[60vh] lg:h-[70vh]">
        {backdropUrl && (
          <>
            <img src={backdropUrl} alt={title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-bg-primary/80 via-transparent to-transparent" />
          </>
        )}
      </div>

      <div className="relative -mt-32 md:-mt-48 px-4 md:px-8 lg:px-12 xl:px-16 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <Card className="overflow-hidden border-none shadow-2xl w-48 md:w-64 lg:w-80">
                <img
                  src={posterUrl}
                  alt={title}
                  className="w-full h-auto aspect-[2/3] object-cover"
                />
              </Card>
            </div>

            <div className="flex-1 space-y-4 md:space-y-6">
              <div>
                <h1 className="font-bebas text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-3 md:mb-4 tracking-tight">
                  {title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-text-secondary font-work text-base md:text-lg">
                  {rating !== 'N/A' && (
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 fill-current" />
                      <span className="text-white font-semibold">{rating}/10</span>
                    </div>
                  )}
                  {releaseYear !== 'N/A' && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 md:w-5 md:h-5" />
                      <span>{releaseYear}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Access required section - Only show if payment mode is enabled AND user needs access */}
              {paymentMode.enabled && needsAccess ? (
                <div className="space-y-4">
                  <div className="p-4 md:p-6 bg-yellow-900/20 border border-yellow-500/50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Lock className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-200 font-work font-semibold mb-2">
                          Acces Premium Requis
                        </p>
                        <p className="text-text-secondary text-sm mb-4">
                          Obtenez 30 jours d'acces illimite pour seulement 8,40 euros
                        </p>
                        <Link to="/compte">
                          <Button className="bg-netflix-red hover:bg-netflix-red-hover w-full md:w-auto">
                            Obtenir l'Acces
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  {canExtendTrial && (
                    <div className="p-4 md:p-6 bg-green-900/20 border border-green-500/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Clock className="w-6 h-6 text-green-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-green-200 font-work font-semibold mb-2">
                            Bonus : +15 Minutes Gratuites !
                          </p>
                          <p className="text-text-secondary text-sm mb-4">
                            Profitez d'une derniere chance avec 15 minutes supplementaires
                          </p>
                          <Button
                            onClick={handleExtendTrial}
                            disabled={extendingTrial}
                            className="bg-green-600 hover:bg-green-700 w-full md:w-auto"
                          >
                            {extendingTrial ? 'Chargement...' : 'Prolonger de 15 min (1 fois)'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Button
                    onClick={handlePlay}
                    className="w-full md:w-auto bg-netflix-red hover:bg-netflix-red-hover text-white font-work font-bold px-6 md:px-8 py-4 md:py-6 text-lg md:text-xl rounded-md transition-colors duration-200 shadow-lg"
                    data-testid="play-movie-btn"
                  >
                    <Play className="w-5 h-5 md:w-6 md:h-6 mr-2 fill-current" />
                    Regarder
                  </Button>
                  
                  {/* Bouton signalement */}
                  <Button
                    onClick={() => setShowReportModal(true)}
                    variant="outline"
                    className="w-full md:w-auto border-white/20 hover:border-white/40 text-text-secondary hover:text-white"
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Signaler un problème
                  </Button>
                  
                  {/* Supprimer + Corriger TMDB - visible uniquement pour test@webeos.com */}
                  {user?.email === 'test@webeos.com' && (
                    <div className="w-full space-y-3 mt-2 p-4 bg-white/5 border border-white/10 rounded-lg">
                      <p className="text-text-secondary text-xs font-work uppercase tracking-wider">Outils Admin</p>
                      
                      {/* Corriger l'affiche TMDB */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Coller le lien TMDB (ex: themoviedb.org/movie/12345)"
                          className="flex-1 bg-bg-tertiary border border-white/20 text-white rounded-md px-3 py-2 text-sm font-inter placeholder-text-tertiary"
                          id="tmdb-override-input"
                          data-testid="tmdb-override-input"
                        />
                        <Button
                          onClick={async () => {
                            const input = document.getElementById('tmdb-override-input');
                            const url = input?.value?.trim();
                            if (!url) { alert('Collez un lien TMDB'); return; }
                            try {
                              const res = await axios.post(`${API}/movies/${movieId}/tmdb-override`, 
                                { tmdb_url: url },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              alert(`Affiche corrigée : ${res.data.title}`);
                              window.location.reload();
                            } catch (err) {
                              alert('Erreur: ' + (err.response?.data?.detail || 'Lien invalide'));
                            }
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4"
                          data-testid="tmdb-override-btn"
                        >
                          Corriger
                        </Button>
                      </div>
                      
                      {/* Supprimer */}
                      <Button
                        onClick={async () => {
                          if (!window.confirm(`Supprimer "${movie.title}" définitivement ?`)) return;
                          try {
                            await axios.delete(`${API}/movies/${movieId}`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            alert('Film supprimé');
                            navigate('/');
                          } catch (err) {
                            alert('Erreur: ' + (err.response?.data?.detail || 'Impossible de supprimer'));
                          }
                        }}
                        className="w-full bg-red-900 hover:bg-red-800 text-white text-sm"
                        data-testid="delete-movie-btn"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Supprimer ce film
                      </Button>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="p-3 bg-red-900/20 border border-red-500/50 rounded-md">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                <CardContent className="p-4 md:p-6">
                  <h2 className="text-xl md:text-2xl font-work font-bold text-white mb-3 md:mb-4 uppercase tracking-wider">
                    Synopsis
                  </h2>
                  <p className="text-text-secondary font-inter text-base md:text-lg leading-relaxed">
                    {overview}
                  </p>
                </CardContent>
              </Card>

              {/* Informations détaillées enrichies TMDB */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Genres */}
                {movie.genres && movie.genres.length > 0 && (
                  <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Film className="w-5 h-5 text-netflix-red" />
                        <h3 className="text-sm font-work font-semibold text-white uppercase tracking-wider">
                          Genres
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {movie.genres.map((genre, index) => (
                          <span key={index} className="px-3 py-1 bg-netflix-red/20 border border-netflix-red/40 rounded-full text-white text-sm">
                            {genre}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Durée */}
                {movie.runtime && (
                  <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock4 className="w-5 h-5 text-netflix-red" />
                        <h3 className="text-sm font-work font-semibold text-white uppercase tracking-wider">
                          Durée
                        </h3>
                      </div>
                      <p className="text-text-secondary font-inter text-lg">
                        {Math.floor(movie.runtime / 60)}h {movie.runtime % 60}min
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Réalisateur */}
                {movie.director && (
                  <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-5 h-5 text-netflix-red" />
                        <h3 className="text-sm font-work font-semibold text-white uppercase tracking-wider">
                          Réalisateur
                        </h3>
                      </div>
                      <p className="text-text-secondary font-inter text-lg">
                        {movie.director}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Date complète */}
                {(movie.release_day || movie.release_month || movie.release_year) && (
                  <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5 text-netflix-red" />
                        <h3 className="text-sm font-work font-semibold text-white uppercase tracking-wider">
                          Date de sortie
                        </h3>
                      </div>
                      <p className="text-text-secondary font-inter text-lg">
                        {movie.release_day && `${movie.release_day} `}
                        {movie.release_month && new Date(0, movie.release_month - 1).toLocaleString('fr-FR', { month: 'long' })}
                        {movie.release_year && ` ${movie.release_year}`}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Budget */}
                {movie.budget && movie.budget > 0 && (
                  <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        <h3 className="text-sm font-work font-semibold text-white uppercase tracking-wider">
                          Budget
                        </h3>
                      </div>
                      <p className="text-text-secondary font-inter text-lg">
                        ${movie.budget.toLocaleString('fr-FR')}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Recettes */}
                {movie.revenue && movie.revenue > 0 && (
                  <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-yellow-400" />
                        <h3 className="text-sm font-work font-semibold text-white uppercase tracking-wider">
                          Recettes
                        </h3>
                      </div>
                      <p className="text-text-secondary font-inter text-lg">
                        ${movie.revenue.toLocaleString('fr-FR')}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Pays de production */}
                {movie.production_countries && movie.production_countries.length > 0 && (
                  <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-5 h-5 text-netflix-red" />
                        <h3 className="text-sm font-work font-semibold text-white uppercase tracking-wider">
                          Pays
                        </h3>
                      </div>
                      <p className="text-text-secondary font-inter text-base">
                        {movie.production_countries.join(', ')}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Acteurs */}
              {movie.cast && movie.cast.length > 0 && (
                <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-netflix-red" />
                      <h2 className="text-xl md:text-2xl font-work font-bold text-white uppercase tracking-wider">
                        Acteurs Principaux
                      </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {movie.cast.map((actor, index) => (
                        <div key={index} className="bg-bg-primary/50 p-3 rounded-lg border border-white/10">
                          <p className="text-white font-inter text-sm">{actor}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {movie.original_title && movie.original_title !== title && (
                <Card className="bg-bg-secondary/80 backdrop-blur-sm border-white/10">
                  <CardContent className="p-4 md:p-6">
                    <h3 className="text-sm font-work font-semibold text-text-secondary uppercase tracking-wider mb-2">
                      Titre Original
                    </h3>
                    <p className="text-white font-inter text-base md:text-lg">
                      {movie.original_title}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}